import React from 'react';

interface Question {
  id: string;
  type: 'yesno' | 'text' | 'select' | 'date' | 'consent';
  title: string;
  description?: string;
  required?: boolean;
  options?: string[];
  validation?: { 
    pattern?: string; 
    minLength?: number; 
    maxLength?: number; 
  };
}

interface ReviewStepProps {
  questions: Question[];
  answers: Record<string, any>;
  onEdit: (questionIndex: number) => void;
  onSubmit: () => void;
  onPrevious: () => void;
  submitting: boolean;
}

export function ReviewStep({ questions, answers, onEdit, onSubmit, onPrevious, submitting }: ReviewStepProps) {
  
  const formatAnswer = (question: Question, answer: any) => {
    if (answer === null || answer === undefined || answer === '') {
      return <span className="text-gray-400 italic">Not answered</span>;
    }

    switch (question.type) {
      case 'yesno':
        return (
          <span className={`font-medium ${answer ? 'text-green-600' : 'text-red-600'}`}>
            {answer ? 'Yes' : 'No'}
          </span>
        );
      
      case 'consent':
        return (
          <span className={`font-medium ${answer ? 'text-green-600' : 'text-gray-600'}`}>
            {answer ? 'Consent given' : 'Consent not given'}
          </span>
        );
      
      case 'date':
        return answer ? new Date(answer).toLocaleDateString() : 'Not provided';
      
      case 'text':
      case 'select':
      default:
        return <span className="text-gray-900">{answer}</span>;
    }
  };

  const getAnsweredCount = () => {
    const requiredQuestions = questions.filter(q => q.required !== false);
    const answeredRequired = requiredQuestions.filter(q => {
      const answer = answers[q.id];
      return answer !== null && answer !== undefined && answer !== '';
    });
    return {
      answered: answeredRequired.length,
      total: requiredQuestions.length,
      allAnswered: answeredRequired.length === requiredQuestions.length
    };
  };

  const { answered, total, allAnswered } = getAnsweredCount();

  return (
    <div className="p-8 md:p-12">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
          <span className="text-gray-500 text-sm font-medium">
            Final Review
          </span>
        </div>
        
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
          Review Your Answers
        </h2>
        
        <p className="text-gray-600 text-lg">
          Please review your responses below. You can edit any answer by clicking on it.
        </p>
      </div>

      {/* Progress Summary */}
      <div className={`
        mb-8 p-4 rounded-lg border-2
        ${allAnswered 
          ? 'border-green-200 bg-green-50' 
          : 'border-amber-200 bg-amber-50'
        }
      `}>
        <div className="flex items-center gap-3">
          {allAnswered ? (
            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ) : (
            <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          )}
          
          <div>
            <span className={`font-semibold ${allAnswered ? 'text-green-800' : 'text-amber-800'}`}>
              {answered} of {total} required questions answered
            </span>
            {!allAnswered && (
              <p className="text-amber-700 text-sm mt-1">
                Please complete all required questions before submitting.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Questions and Answers */}
      <div className="space-y-6 mb-8">
        {questions.map((question, index) => {
          const answer = answers[question.id];
          const isAnswered = answer !== null && answer !== undefined && answer !== '';
          const isRequired = question.required !== false;

          return (
            <div
              key={question.id}
              className={`
                border-2 rounded-lg p-6 transition-all duration-200 cursor-pointer hover:shadow-md
                ${isAnswered 
                  ? 'border-green-200 bg-green-50/50' 
                  : isRequired
                    ? 'border-red-200 bg-red-50/50'
                    : 'border-gray-200 bg-gray-50/50'
                }
                hover:border-amber-300 hover:bg-amber-50/50
              `}
              onClick={() => onEdit(index)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-medium text-gray-500">
                      Question {index + 1}
                    </span>
                    {isRequired && (
                      <span className="text-red-500 text-sm">*</span>
                    )}
                    <div className={`
                      w-2 h-2 rounded-full
                      ${isAnswered ? 'bg-green-500' : isRequired ? 'bg-red-500' : 'bg-gray-300'}
                    `} />
                  </div>
                  
                  <h3 className="font-semibold text-gray-900 mb-2 text-lg">
                    {question.title}
                  </h3>
                  
                  <div className="text-lg">
                    {formatAnswer(question, answer)}
                  </div>
                </div>
                
                <button
                  className="ml-4 p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-100 rounded-lg transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(index);
                  }}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between items-center pt-6 border-t border-gray-200">
        <button
          onClick={onPrevious}
          className="flex items-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 hover:shadow-md transition-all duration-200"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Questions
        </button>

        <button
          onClick={onSubmit}
          disabled={!allAnswered || submitting}
          className={`
            flex items-center gap-2 px-8 py-3 rounded-lg font-medium transition-all duration-200 min-w-[160px] justify-center
            ${allAnswered && !submitting
              ? 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700 hover:shadow-lg transform hover:scale-105'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }
          `}
        >
          {submitting ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              Submit Questionnaire
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </>
          )}
        </button>
      </div>

      {/* Final submission notice */}
      {allAnswered && (
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="text-blue-800 text-sm">
              <p className="font-medium mb-1">Ready to submit</p>
              <p>
                Once submitted, your responses will be sent to your advisor for review. 
                You will receive a confirmation email shortly after submission.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}